import React, { useEffect, useState } from 'react';
import { Copy, Download, Hash } from 'lucide-react';
import * as api from '../../api';
import { Btn, Input, Select } from '../UI';
import { Panel } from '../materialTakeoff/MTOShared';
import { exportXlsx } from '../materialMapping/mmUtils';

const pad = (n, w) => String(n).padStart(w, '0');

// Issue the next main codes of a discipline, or sub-codes under a main code.
export default function CGGenerate({ projectId, settings, disciplines, version, onChanged, canEdit, toast }) {
  const [disc, setDisc] = useState('');
  const [count, setCount] = useState('10');
  const [desc, setDesc] = useState('');
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState('');
  const [issued, setIssued] = useState([]);
  // sub-codes
  const [mains, setMains] = useState([]);
  const [parent, setParent] = useState('');
  const [subCount, setSubCount] = useState('3');
  const [subDesc, setSubDesc] = useState('');

  useEffect(() => { api.getCodeSummary(projectId).then(setSummary).catch(() => setSummary(null)); }, [projectId, version]);
  useEffect(() => {
    api.listBoqCodes({ project_id: projectId, kind: 'main', limit: 10000 })
      .then(r => setMains((r || []).filter(c => c.status !== 'void'))).catch(() => setMains([]));
  }, [projectId, version]);

  const active = disciplines.filter(d => d.is_active);
  const last = summary?.disciplines.find(d => d.code === disc)?.last_number || 0;
  const n = Math.max(0, Math.min(500, parseInt(count, 10) || 0));
  const prefix = settings?.prefix;
  const preview = disc && n ? [last + 1, last + n].filter((x, i, a) => a.indexOf(x) === i).map(x => `${prefix}-${disc}-${pad(x, 4)}`) : [];

  const parentCode = mains.find(m => m.id === parent);
  const sn = Math.max(0, Math.min(99, parseInt(subCount, 10) || 0));
  const subStart = (parentCode?.sub_count || 0) + 1;

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const codes = await fn();
      setIssued(codes || []);
      toast?.show?.(`${(codes || []).length} code(s) issued`, 'success');
      onChanged();
    } catch (e) {
      toast?.show?.(e.message || 'Could not generate codes', 'error');
    }
    setBusy('');
  };

  const copy = () => {
    navigator.clipboard?.writeText(issued.map(c => c.code).join('\n'));
    toast?.show?.('Codes copied — paste them into the BOQ CODE column', 'success');
  };

  if (!canEdit) return <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Only project managers, quantity surveyors and administrators can issue codes.</div>;

  return (
    <div>
      <Panel title="Main codes">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
          <Select label="Discipline" value={disc} onChange={setDisc}
                  options={[{ value: '', label: '— Select —' }, ...active.map(d => ({ value: d.code, label: `${d.code} — ${d.name}` }))]} />
          <Input label="How many (1–500)" type="number" min="1" max="500" value={count} onChange={setCount} />
          <div style={{ gridColumn: 'span 2' }}><Input label="Description (optional)" value={desc} onChange={setDesc} placeholder="e.g. Lighting — Block A" /></div>
        </div>
        {disc && (
          <div style={{ marginTop: 12, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Hash size={15} color="#0b318b" />
            {n ? <>Will issue <b style={{ fontFamily: 'monospace' }}>{preview[0]}</b>{n > 1 && <> to <b style={{ fontFamily: 'monospace' }}>{preview[1]}</b></>} ({n} code{n === 1 ? '' : 's'})</> : 'Enter how many.'}
            <span style={{ color: 'var(--text-secondary)' }}>· last issued {disc}: {last ? pad(last, 4) : 'none'}</span>
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          <Btn disabled={!disc || !n} loading={busy === 'main'}
               onClick={() => run('main', () => api.generateBoqCodes({ project_id: projectId, discipline_code: disc, count: n, description: desc || null }))}>
            Generate {n || ''} Code{n === 1 ? '' : 's'}
          </Btn>
        </div>
      </Panel>

      <Panel title="Sub-codes under a main code">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <Select label="Main code" value={parent} onChange={setParent}
                    options={[{ value: '', label: mains.length ? '— Select —' : 'No main codes yet' },
                      ...mains.map(m => ({ value: m.id, label: `${m.code}${m.description ? ` — ${m.description}` : ''}${m.sub_count ? ` (${m.sub_count} sub)` : ''}` }))]} />
          </div>
          <Input label="How many (1–99)" type="number" min="1" max="99" value={subCount} onChange={setSubCount} />
          <Input label="Description (optional)" value={subDesc} onChange={setSubDesc} placeholder="defaults to the main code's" />
        </div>
        {parentCode && sn > 0 && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            Will issue <b style={{ fontFamily: 'monospace' }}>{parentCode.code}-{pad(subStart, 2)}</b>
            {sn > 1 && <> to <b style={{ fontFamily: 'monospace' }}>{parentCode.code}-{pad(subStart + sn - 1, 2)}</b></>}
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          <Btn disabled={!parent || !sn} loading={busy === 'sub'}
               onClick={() => run('sub', () => api.generateSubCodes(parent, { count: sn, description: subDesc || null }))}>Generate Sub-codes</Btn>
        </div>
      </Panel>

      {issued.length > 0 && (
        <Panel title={`Just issued (${issued.length})`}
               right={<div style={{ display: 'flex', gap: 8 }}>
                 <Btn variant="secondary" onClick={copy}><Copy size={14} /> Copy</Btn>
                 <Btn variant="secondary" onClick={() => exportXlsx(issued, [{ key: 'code', label: 'BOQ CODE' }, { key: 'description', label: 'Description' }],
                                                                     `BOQ-codes-${issued[0].discipline_code}`, 'Codes')}><Download size={14} /> Excel</Btn>
               </div>}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {issued.map(c => (
              <span key={c.id} style={{ fontFamily: 'monospace', fontSize: 12.5, fontWeight: 700, padding: '5px 10px', borderRadius: 8,
                                        background: 'rgba(11,49,139,0.08)', color: '#0b318b', border: '1px solid rgba(11,49,139,0.2)' }}>{c.code}</span>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '10px 0 0' }}>
            These codes are reserved for this project. Put them in a BOQ (form or Excel import) and they become “On BOQ” automatically.
          </p>
        </Panel>
      )}
    </div>
  );
}
